import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";

/**
 * Is the operator's flight-tracking add-on on? Mirrors the rider app's `useFlightTrackingEnabled`
 * (DEV-1497, mobile #117): the add-on, not the pickup address, is what decides whether the flight flow
 * appears at all. The backend refuses to register a flight for tracking when it is off
 * (`BookingFlightAlertListener`), so deriving a pickup from an arrival promises an update that never comes.
 *
 * `enabled` defaults to FALSE. Defaulting ON while the operator is unknown fires
 * `GET /v3/client/{account}/flight` for operators that may never have bought the add-on, and unknown is
 * not a licence to spend someone's Cirium quota — so do not restore a `?? true` here.
 *
 * `isResolving` is the third state and it is only meaningful where a resolve can actually be OBSERVED:
 *
 *   - AUTHED PORTAL: always false, and that is not a shortcut. `ClientAccountProvider` renders `<Loading />`
 *     until `useSetupClientAccount` reports ready, and ready includes `brandingReady`, which is set in the
 *     `.finally()` after its `verifyProvider` settles. Nothing in the booking sheet can mount before the
 *     operator has resolved, so there is no window left to hold for — the holding already happened, one
 *     level up. That same `verifyProvider` is what writes `activeOperator`, which is what makes `?? false`
 *     safe: an absent add-on means the resolved payload did not state one, not that nobody has looked yet.
 *   - GUEST: genuinely unresolved for a tick. The provider is resolved from the quote or widget link
 *     AFTER first render (`useHydrateBookingFromQuote`, `AuthedWidgetRedirect`), so an absent operator
 *     there really does mean "not known yet".
 *
 * Deliberately NOT a deadlock: every branch has a terminal event. The authed branch is false by
 * construction, and the guest branch ends when the provider resolves or the flow navigates to /404 —
 * guest booking cannot proceed without a provider at all. The rider app needed a `Boolean(uuid)` qualifier
 * for exactly this reason; the equivalent here is that neither branch can wait on an event that never fires.
 *
 * The split callers want is the rider app's, between a LOOKUP and a COMMIT:
 *
 *   - The lookup is gated on `enabled` ALONE, never `enabled || isResolving`. That matches
 *     `PickupDateTimeScreen`, the screen this panel corresponds to, by explicit decision there: a brief
 *     manual-picker flash for an enabled operator beats spending a request for an operator that never
 *     bought the add-on.
 *   - Anything that COMMITS holds while `isResolving`. Skipping the lookup makes `isFlightFetching` false,
 *     so the flight card's own "still loading" guard cannot see the guest resolve window at all — and a
 *     Proceed inside it would commit the seeded manual time, with no wait and no landing, moments before
 *     the flight card appears above a pickup that was never derived from it.
 */
export const useFlightTrackingEnabled = (): { enabled: boolean; isResolving: boolean } => {
  const activeOperator = useAppSelector(activeOperatorSelector);
  const { isGuest } = useGuestBooking();

  return {
    enabled: activeOperator?.addOns?.flightTracking ?? false,
    isResolving: isGuest && !activeOperator,
  };
};
